import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import { createTheme, ThemeProvider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import { object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import { sub } from 'date-fns';
import InputTheme from '../../Common/Helpers/InputTheme';
import { InputMaskDate } from '../../../Helpers/Formatters';
import MaleIcon from '../../../../icons/male.svg';
import FemaleIcon from '../../../../icons/female.svg';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg';
import WorkerEmployeeIcon from '../../../../icons/workerEmployee.svg';
import WorkerBusinessOwnerIcon from '../../../../icons/workerBusinessOwner.svg';
import WorkerIndependentIcon from '../../../../icons/workerIndependent.svg';
import './AboutMe.scss';

export default function AboutMeForm(props) {
  
  const { onNext, ageSpecs} = props;
  const basicInfo = getBasicInfo();
  const [formState, setFormState] = useState(AboutMeForm.DefaultFormState(false, basicInfo));

  const { register, errors, handleSubmit, triggerValidation, setValue } = useForm({
    validationSchema: AboutMeForm.ValidationSchema(ageSpecs),
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const onSubmit = (s) => {
    var basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = formState.gender;
    basicInfoFromSessionStorage.birthdate = formState.birthdate;
    basicInfoFromSessionStorage.state = formState.state;
    basicInfoFromSessionStorage.tobacco = formState.tobacco;
    basicInfoFromSessionStorage.employment = formState.employment;

    setBasicInfo(basicInfoFromSessionStorage);
    onNext();
  };

  useEffect(() => {
    register({name: "gender"});
    setValue("gender", formState.gender);
    register({name: "birthdate"});
    setValue("birthdate", formState.birthdate);
    register({name: "state"});
    setValue("state", formState.state);
    register({name: "tobacco"});
    setValue("tobacco", formState.tobacco);
    register({name: "employment"});
    setValue("employment", formState.employment);
  }, [register]);

  const handleValueChange = (field, value) => {
    setValue(field, value)
    setFormState({...formState, [field]: value})
  };

  const handleGenderValueChange = (field, value) => {
    handleValueChange(field, value);
    triggerValidation(field);
  };

  const handleEmploymentValueChange = (field, value) => {
    handleValueChange(field, value);
    triggerValidation(field);
  };

  const getEmploymentOptions = [
    {
      label: "Employee",
      value: "Employee",
      secondaryText: "I work as a w-2 employee for a company.",
      icon: WorkerEmployeeIcon,
    },
    {
      label: "Business Owner",
      value: "Business",
      secondaryText: "I am an owner or partner in a business.",
      icon: WorkerBusinessOwnerIcon,
    },
    {
      label: "Independent Contractor",
      value: "Independent",
      secondaryText: "I work primarily as a 1099 independent contractor or am a freelancer.",
      icon: WorkerIndependentIcon,
    },
  ];

  const getTobaccoOptions = [
    {
      label: "Currently or previously used",
      value: "true",
    },
    {
      label: "Never used",
      value: "false",
    },
  ];

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <div className="basic-info-about-me income d-flex justify-content-center">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="justify-content-center">
            <Col lg={12}>
              <StyledRadioInput
                name="gender"
                id="gender"
                value={formState.gender}
                options={[
                  { value: "F", label: "Female", icon: <FemaleIcon /> }, 
                  { value: "M", label: "Male", icon: <MaleIcon /> }
                ]}
                onChange={e => handleGenderValueChange('gender', e)}
                errors={errors && errors.gender}
                isBlankIcon={true}
                isBlankCheckMark={true}
              />
              <Form.Group>
                <TextInput 
                  name="birthdate"
                  id="birthdate"
                  label="Date of Birth"
                  value={formState.birthdate}
                  errors={errors && errors.birthdate}
                  inputRef={register}
                  onChange={e => handleValueChange("birthdate", e.target.value)}
                  InputProps={{inputComponent: InputMaskDate,}}
                />
              </Form.Group>
              <Form.Group>
                <SelectInput 
                  name="state"
                  id="state"
                  label="State"
                  value={formState.state}
                  onChange={e => handleValueChange("state", e.target.value)}
                  error={errors && errors.state}
                  inputRef={register}
                  selectOptions={States()}
                />
              </Form.Group>
              <Form.Group>
                <SelectInput 
                  name="tobacco"
                  id="tobacco"
                  label="Tobacco/Nicotine Use"
                  value={formState.tobacco}
                  onChange={e => handleValueChange("tobacco", e.target.value)}
                  error={errors && errors.tobacco}
                  hideEmptyOption
                  selectOptions={getTobaccoOptions}
                />
              </Form.Group>
              <Form.Group>
                <SelectInput
                  name="employment"
                  id="employment"
                  label="What type of worker are you?"
                  value={formState.employment}
                  onChange={e => handleEmploymentValueChange("employment", e.target.value)}
                  selectOptions={getEmploymentOptions}
                  errors={errors && errors.employment}
                  inputRef={register}
                  hideEmptyOption
                  isImageOptions={true}
                  renderValue={value => value}
                  MenuProps={{disableScrollLock: true}}
                />
              </Form.Group>
            </Col>
            <Col className="d-flex m-0">
              <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'> <ShieldCheckmarkIcon className="button-icon" /> QUICK QUOTE</Button>
            </Col>
          </Row>
        </Form>
      </div>
    </ThemeProvider>
  )
};

AboutMeForm.propTypes = {
  onNext: PropTypes.func.isRequired,
  ageSpecs: PropTypes.object.isRequired
};

AboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required("Gender is required."),
    birthdate: date()
      .typeError("Invalid date.")
      .max(sub(new Date(), {years: ageSpecs.min, days: ageSpecs.minAgeDays || 0}, `Minimum age is ${ageSpecs.minAgeDays ? ageSpecs.minAgeDays + ' days' : ageSpecs.min}.`))
      .min(sub(new Date(), {years: ageSpecs.max + 1}), `Maximum age is ${ageSpecs.max}.`)
      .required("Birtday is required"),
    state: string().required("State Code is required."),
    tobacco: string().required("This field is required."),
    employment: string().required("This field is required."),
  })
};

AboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'false' : (basicInfo && basicInfo.tobacco) || 'false',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    employment: prefill ? 'Employee' : (basicInfo && basicInfo.employment) || '',
  };
};


// WEBPACK FOOTER //
// ./src/components/Quoters/LongTerm/steps/AboutMeForm.js